import React from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer
} from 'recharts';

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];
const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const formatMonthLabel = (yyyymm) => {
  const match = yyyymm?.match(/^(\d{4})-(\d{2})$/);
  if (!match) return yyyymm;
  const idx = parseInt(match[2]) - 1;
  return `${MONTH_ABBR[idx]} ${match[1]}`;
};

const linearRegression = (x, y) => {
  const n = x.length;
  const sumX = x.reduce((a, b) => a + b, 0);
  const sumY = y.reduce((a, b) => a + b, 0);
  const sumXY = x.reduce((s, xi, i) => s + xi * y[i], 0);
  const sumX2 = x.reduce((s, xi) => s + xi * xi, 0);
  const denom = n * sumX2 - sumX * sumX;
  if (denom === 0) return { slope: 0, intercept: sumY / n, r2: 0 };
  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;
  const yMean = sumY / n;
  const ssTot = y.reduce((s, yi) => s + Math.pow(yi - yMean, 2), 0);
  const ssRes = y.reduce((s, yi, i) => s + Math.pow(yi - (slope * x[i] + intercept), 2), 0);
  const r2 = ssTot > 0 ? Math.max(0, Math.min(1, 1 - ssRes / ssTot)) : 0;
  return { slope, intercept, r2 };
};

export const processChartData = (rawData) => {
  if (!rawData || rawData.length === 0) return null;

  const sorted = [...rawData]
    .filter(d => d.month && !isNaN(parseFloat(d.income)))
    .sort((a, b) => a.month.localeCompare(b.month));

  if (sorted.length === 0) return null;

  const chartItems = sorted.map((d, i) => ({
    month: d.month,
    label: formatMonthLabel(d.month),
    income: Math.round(parseFloat(d.income) * 100) / 100,
    index: i,
  }));

  const incomes = chartItems.map(d => d.income);
  const totalRevenue = incomes.reduce((a, b) => a + b, 0);
  const avgMonthly = totalRevenue / incomes.length;
  const variance = incomes.reduce((s, v) => s + Math.pow(v - avgMonthly, 2), 0) / incomes.length;
  const variancePct = avgMonthly > 0 ? (Math.sqrt(variance) / avgMonthly) * 100 : 0;

  const maxIncome = Math.max(...incomes);
  const minIncome = Math.min(...incomes);
  const highestMonth = chartItems[incomes.indexOf(maxIncome)];
  const lowestMonth = chartItems[incomes.indexOf(minIncome)];

  let forecastItems = [];
  let forecastStats = null;

  if (chartItems.length >= 4) {
    const xVals = chartItems.map(d => d.index);
    const { slope, intercept, r2 } = linearRegression(xVals, incomes);

    const lastItem = chartItems[chartItems.length - 1];
    const [lastYear, lastMonthNum] = lastItem.month.split('-').map(Number);

    for (let i = 1; i <= 6; i++) {
      const futureIndex = lastItem.index + i;
      const predicted = Math.max(slope * futureIndex + intercept, 0);
      const futureMonthNum = ((lastMonthNum - 1 + i) % 12) + 1;
      const futureYear = lastYear + Math.floor((lastMonthNum - 1 + i) / 12);
      const futureMonth = `${futureYear}-${String(futureMonthNum).padStart(2, '0')}`;
      forecastItems.push({
        month: futureMonth,
        label: formatMonthLabel(futureMonth),
        income: Math.round(predicted * 100) / 100,
        index: futureIndex,
      });
    }

    const firstF = forecastItems[0].income;
    const lastF = forecastItems[forecastItems.length - 1].income;
    const trendPct = firstF > 0 ? ((lastF - firstF) / firstF) * 100 : 0;

    forecastStats = {
      r2,
      confidence: Math.round(r2 * 100),
      trendDirection: trendPct > 5 ? 'increasing' : trendPct < -5 ? 'decreasing' : 'stable',
      trendPct: Math.abs(trendPct),
      projectedAvg: forecastItems.reduce((s, d) => s + d.income, 0) / forecastItems.length,
      forecastPeriod: `${forecastItems[0].label} — ${forecastItems[forecastItems.length - 1].label}`,
    };
  }

  return {
    chartItems,
    forecastItems,
    forecastStats,
    totalRevenue,
    avgMonthly,
    variancePct,
    highestMonth,
    lowestMonth,
    stabilityRating: variancePct < 15 ? 'High' : variancePct < 30 ? 'Moderate' : 'Variable',
    monthCount: chartItems.length,
  };
};

const fmt = (val) =>
  val != null
    ? `$${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '';

const fmtAxis = (val) =>
  val >= 1000 ? `$${(val / 1000).toFixed(0)}k` : `$${val}`;

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const item = payload.find(p => p.value != null);
  if (!item) return null;
  const isForecast = item.dataKey === 'forecast';
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-bold text-gray-900">{label}</p>
      <p style={{ color: isForecast ? '#ff6600' : '#0066cc' }}>
        {isForecast ? 'Forecast' : 'Actual'}: {fmt(item.value)}
      </p>
    </div>
  );
};

const IncomeByMonthChart = ({ chartData, pdfMode = false }) => {
  if (!chartData) return null;

  const {
    chartItems, forecastItems, forecastStats,
    totalRevenue, avgMonthly, variancePct,
    stabilityRating, monthCount
  } = chartData;

  // Build unified data array: historical points have `historical`, forecast points have `forecast`
  // The last historical point also gets a `forecast` value to connect the two lines visually
  const combinedData = [
    ...chartItems.map(d => ({ label: d.label, historical: d.income, forecast: null })),
    ...forecastItems.map(d => ({ label: d.label, historical: null, forecast: d.income })),
  ];
  if (forecastItems.length > 0 && chartItems.length > 0) {
    combinedData[chartItems.length - 1] = {
      ...combinedData[chartItems.length - 1],
      forecast: chartItems[chartItems.length - 1].income,
    };
  }

  const trendColor =
    forecastStats?.trendDirection === 'increasing' ? '#16a34a'
    : forecastStats?.trendDirection === 'decreasing' ? '#dc2626'
    : '#6b7280';

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      {!pdfMode && (
        <>
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-xl font-bold text-gray-900">Monthly Revenue</h2>
          </div>
          <p className="text-gray-600 text-sm mb-4">
            {forecastItems.length > 0
              ? 'Historical revenue with 6-month AI forecast'
              : 'Monthly revenue trend'}
          </p>

          {/* KPI Tiles */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-500 font-semibold uppercase">Total Revenue</p>
              <p className="text-xl font-bold text-blue-700">{fmt(totalRevenue)}</p>
              <p className="text-xs text-gray-400">{monthCount} months</p>
            </div>
            <div className="bg-green-50 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-500 font-semibold uppercase">Avg / Month</p>
              <p className="text-xl font-bold text-green-700">{fmt(avgMonthly)}</p>
            </div>
            <div className="bg-purple-50 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-500 font-semibold uppercase">Variance</p>
              <p className="text-xl font-bold text-purple-700">{variancePct.toFixed(1)}%</p>
              <p className="text-xs text-gray-400">{stabilityRating} stability</p>
            </div>
            <div className="bg-orange-50 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-500 font-semibold uppercase">Trend</p>
              {forecastStats ? (
                <>
                  <p className="text-xl font-bold" style={{ color: trendColor }}>
                    {forecastStats.trendDirection.charAt(0).toUpperCase() + forecastStats.trendDirection.slice(1)}
                  </p>
                  <p className="text-xs text-gray-400">{forecastStats.trendPct.toFixed(1)}% projected</p>
                </>
              ) : (
                <p className="text-sm text-gray-400 mt-2">Need 4+ months</p>
              )}
            </div>
          </div>
        </>
      )}

      {/* Chart */}
      <ResponsiveContainer width="100%" height={340}>
        <LineChart data={combinedData} margin={{ top: 10, right: 20, left: 10, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11 }}
            interval={0}
            angle={-35}
            textAnchor="end"
            height={60}
          />
          <YAxis tickFormatter={fmtAxis} tick={{ fontSize: 11 }} width={70} />
          <Tooltip content={<CustomTooltip />} />
          <Line
            dataKey="historical"
            stroke="#0066cc"
            strokeWidth={2.5}
            dot={{ r: 4, fill: '#0066cc' }}
            connectNulls={false}
            name="Actual"
          />
          {forecastItems.length > 0 && (
            <Line
              dataKey="forecast"
              stroke="#ff6600"
              strokeWidth={2.5}
              strokeDasharray="6 4"
              dot={{ r: 4, fill: '#ff6600' }}
              connectNulls={false}
              name="Forecast"
            />
          )}
        </LineChart>
      </ResponsiveContainer>

      {/* Legend */}
      {forecastItems.length > 0 && (
        <div className="flex items-center justify-center gap-6 mt-3">
          <div className="flex items-center gap-2">
            <div style={{ width: 22, height: 3, backgroundColor: '#0066cc', borderRadius: 2 }} />
            <span className="text-xs text-gray-600">Actual Revenue</span>
          </div>
          <div className="flex items-center gap-2">
            <div style={{ width: 22, height: 0, borderTop: '3px dashed #ff6600' }} />
            <span className="text-xs text-gray-600">6-Month Forecast</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncomeByMonthChart;
